import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import validationSchema from 'src/utils/validationSchema';

import { useTranslate } from 'src/locales';
import { useChannelContext } from 'src/pages/channel/hooks';
import { useGoogleAuthPopup } from 'src/pages/channel/channels/Email/hooks/useGoogleAuthPopup';
import { useMicroSoftAuthPopup } from 'src/pages/channel/channels/Email/hooks/useMicroSoftAuthPopup';

export type CampaignField = { channelName: string; email: string };

export function useEmailStep3() {
  const { onChangeStep, state: channelState, handleEmailChannelDtls } = useChannelContext();
  const { channelName, email } = channelState.emailChannelDtls || { channelName: '', email: '' };

  const { loginWithMicrosoft, isMSAccVerified } = useMicroSoftAuthPopup();
  const { loginWithGoogle, isGoogleAccVerified } = useGoogleAuthPopup();

  const { t } = useTranslate('translation');

  const emailProviderType = channelState.emailProviderType;

  const fldList = [
    { name: 'channelName', required: true },
    { name: 'email', required: emailProviderType === 'other_provider' },
  ];

  const CampaignSchema = validationSchema(fldList, t);

  const defaultValues: CampaignField = {
    channelName: '',
    email: '',
  };

  const methods = useForm<CampaignField>({
    mode: 'all',
    resolver: zodResolver(CampaignSchema),
    defaultValues,
    values: { channelName, email },
  });

  const {
    reset,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
    getValues,
  } = methods;

  const [watchedChannelName, watchedEmail] = watch(['channelName', 'email']);

  const onSubmit = handleSubmit(async (data) => {
    await handleEmailChannelDtls(data);
    reset();
    onChangeStep('next');
  });

  const signInBtnHandler = () => {
    const channelValue = getValues('channelName');
    handleEmailChannelDtls({ ...channelState.emailChannelDtls, channelName: channelValue });
    if (emailProviderType === 'google') {
      loginWithGoogle();
    } else if (emailProviderType === 'microsoft') {
      loginWithMicrosoft();
    }
  };

  const isNextDisabled = () => {
    if (emailProviderType === 'other_provider') {
      return !watchedChannelName || !watchedEmail || !!errors.email || isSubmitting;
    }

    return !watchedChannelName || (!isGoogleAccVerified && !isMSAccVerified) || isSubmitting;
  };

  const getProviderDisplayName = () => {
    switch (emailProviderType) {
      case 'google':
        return t('GOOGLE');
      case 'microsoft':
        return t('MICROSOFT');
      default:
        return t('OTHER_PROVIDER');
    }
  };

  return {
    methods,
    onSubmit,
    signInBtnHandler,
    isNextDisabled,
    getProviderDisplayName,
    emailProviderType,
    isGoogleAccVerified,
    isMSAccVerified,
    watchedChannelName,
    isSubmitting,
    onChangeStep,
    t,
  };
}
